import React from 'react'
import styled from 'styled-components'

const TermsText = styled.div`
  padding: 1rem;

  p {
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.25rem;
  }

  h3 {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    text-decoration: underline;
  }

  a {
    color: rgb(99, 174, 255);
    text-decoration: underline;
  }
`

export const TermsContent: React.FC = () => {
  return (
    <>
      <TermsText>
        <p style={{ marginBottom: '1rem' }}>Last Updated: July 7, 2025</p>
        <p>
          This Terms of Service (&quot;TOS&quot;) is a legal agreement between
          you (&quot;End-User&quot; or &quot;you&quot;) and Gecko Robotics, Inc.
          (&quot;Gecko&quot;). By accessing or using Cantilever®, you agree to
          be bound by the terms of this TOS.
        </p>

        <h3>Acknowledgment</h3>
        <p>
          The End-User acknowledges that this TOS is solely between Gecko and
          the End-User, and not with any third party. Gecko, not any third
          party, is solely responsible for Cantilever and its content. This TOS
          does not provide for usage rules for Cantilever or the use of any
          third-party license agreement that exceed or conflict with the terms
          of any applicable license agreement.
        </p>

        <h3>User Conduct</h3>
        <p>
          You agree to use Cantilever only for lawful purposes and in accordance
          with these TOS and any agreement between Gecko and your enterprise.
          You must not use Cantilever to (i) post or transmit any material that
          is unlawful, threatening, abusive, or otherwise objectionable, (ii)
          violate any applicable laws or regulations, or (iii) interfere with or
          disrupt, or attempt to interfere with or disrupt, the integrity of the
          performance of Cantilever.
        </p>

        <h3>Scope of License</h3>
        <p>
          Any license granted to the End-User to any third party application is
          a non-transferable license to use such product(s) solely for the
          End-User&apos;s personal and internal use. This license expressly
          prohibits the copying, separation, unbundling, extraction, isolation,
          removal, or distribution of the materials contained herein.
        </p>

        <h3>Maintenance and Support</h3>
        <p>
          Gecko is solely responsible for providing any maintenance and support
          services for Cantilever. The End-User acknowledges that no third party
          has any obligation to furnish any maintenance or support services to
          you for Cantilever or any integrated third party products integrated
          herein.
        </p>

        <h3>Warranty</h3>
        <p>
          Gecko is solely responsible for any product warranties, whether
          express or implied by law. To the maximum extent permitted by
          applicable law, all materials included in any third party products are
          provided &quot;as is&quot; without any representations or warranties
          of any kind. All warranties, express or implied, oral or written, are
          disclaimed and excluded, including implied warranties of
          merchantability, title, non-infringement, and fitness for a particular
          purpose. Third party content providers, including Maxar, have no
          warranty obligations to End-User for the products.
        </p>

        <h3>Disclaimer</h3>
        <p>
          Cantilever may contain various modules that provide insights or
          recommendations to End-User (the &quot;Insights&quot;). Any Insights
          provided in the application are derived from relevant codes,
          standards, industry practices, and parameters provided by End-User,
          and reflect the conditions as observed at the time of data collection.
          The Insights are intended to aid in decision-making processes;
          however, they should not serve as the sole basis for final decisions
          concerning repair, maintenance, and/or operations. It is important to
          note that subsequent events or conditions occurring after the
          inspection date may significantly impact the accuracy and relevance of
          the Insights, and the Insights are intended solely for informational
          purposes. GECKO ASSUMES NO RESPONSIBILITY FOR ANY ACTIONS TAKEN BASED
          ON THE INSIGHTS, NOR FOR THE IMPLEMENTATION OR QUALITY OF ANY REPAIRS
          OR OPERATIONAL DECISIONS UNDERTAKEN. WE STRONGLY RECOMMEND CONSULTING
          QUALIFIED LICENSED PROFESSIONALS WHEN FORMULATING REPAIR, MAINTENANCE,
          AND/OR OPERATIONAL STRATEGIES.
        </p>

        <h3>Product Claims</h3>
        <p>
          Gecko is responsible for addressing any claims related to Cantilever
          or the End-User&apos;s use of it, including product liability claims,
          compliance with legal or regulatory requirements, and claims under
          consumer protection, privacy, or similar legislation. Unless
          prohibited by law, neither Gecko nor its licensors or suppliers will
          be liable for any incidental, consequential, special, exemplary, or
          indirect damages (including lost profits or data) arising from or
          relating to Cantilever.
        </p>

        <h3>Intellectual Property Rights</h3>
        <p>
          The content and materials within Cantilever are made available to
          End-User via a subscription basis and/or via a term license, not sold.
          All worldwide intellectual property and proprietary rights in
          Cantilever are the exclusive property of Gecko or its third party
          licensors. All rights not expressly granted to the End-User are
          reserved by Gecko, Gecko&apos;s licensors and suppliers. Gecko is
          responsible for any investigation, defense, settlement, and discharge
          of third-party intellectual property infringement claims. Except by
          utilizing any data export functionality within Cantilever, you may not
          modify, reproduce, distribute, create derivative works of, publicly
          display, or otherwise exploit any content within the Application.
        </p>

        <h3>AI Usage Terms</h3>
        <p role='text'>
          Portions of Cantilever may enable End-User to interact with large
          language models and other features that incorporate artificial
          intelligence algorithms and other technologies (collectively, &quot;AI
          Features&quot;). Any usage of such AI Features shall be governed by
          the Artificial Intelligence Model Usage Terms located at{' '}
          <a
            href='https://www.geckorobotics.com/ai-terms'
            target='_blank'
            rel='noopener noreferrer'>
            www.geckorobotics.com/ai-terms
          </a>{' '}
          in addition to this TOS.
        </p>

        <h3>Legal Compliance</h3>
        <p>
          The End-User represents and warrants that they are not located in a
          country subject to a U.S. Government embargo or designated as a
          &quot;terrorist supporting&quot; country and are not listed on any
          U.S. Government list of prohibited or restricted parties.
        </p>

        <h3>Gecko Contact Information</h3>
        <p role='text'>
          For any questions or comments regarding Cantilever, please contact us
          at{' '}
          <a href='mailto:support@geckorobotics.com'>
            support@geckorobotics.com
          </a>
          .
        </p>

        <h3>Third-Party Beneficiary</h3>
        <p>
          Gecko and End-User acknowledge that certain third parties, including
          Maxar, are owners of certain licensed content contained within
          Cantilever, are third-party beneficiaries of this TOS, and as such may
          have the right to enforce the TOS against the End-User as a
          third-party beneficiary.
        </p>

        <h3>Change of Terms</h3>
        <p>
          We may modify these TOS from time to time. Any changes will be
          effective immediately upon the posting of the revised terms. Your
          continued use of Cantilever after changes are posted constitutes your
          acceptance of the modified terms.
        </p>

        <h3>Governing Law</h3>
        <p>
          These TOS shall be governed by and construed in accordance with the
          internal laws of the state of Delaware, USA, without regard to any
          principles of conflict of laws.
        </p>
      </TermsText>
    </>
  )
}
